import { clearSupplierClaimClientSession } from '@/features/supplier-claim/api/supplierClaim.api';
import type { AccountInfo } from '@azure/msal-browser';
import { useMsal } from '@azure/msal-react';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';

/** MSAL may attach the raw id token on the account in some flows; not always present. */
type AccountInfoWithIdToken = AccountInfo & { idToken?: string };

/**
 * Hook to force B2C to re-run the authentication policy.
 *
 * After the backend inserts a CompanySwitch record we need B2C to issue
 * a brand-new id_token whose extension_companyid claim reflects the
 * switched company. The steps are:
 *
 *  1. Wipe the React Query cache (stale company-specific data) and
 *     supplier-claim session keys (staging/prefill must not follow another company).
 *  2. Clear MSAL's local token/account cache so it cannot short-circuit
 *     with a cached token that still carries the old companyId.
 *  3. Trigger loginRedirect — the user's B2C SSO cookie is still valid
 *     so they won't see a login screen, but B2C will re-run the full
 *     policy (including the Azure Function that reads CompanySwitches)
 *     and return a fresh token.
 */
export const useRefreshClaims = () => {
  const { instance, accounts } = useMsal();
  const queryClient = useQueryClient();

  const refreshClaims = useCallback(async () => {
    const account = accounts[0];

    if (!account) {
      throw new Error('No active account found');
    }

    if (import.meta.env.DEV) {
      const withToken = account as AccountInfoWithIdToken;
      // Dev-only: helps verify claims before B2C silent re-auth (e.g. after registration step 1).
      // Do not enable full token logging in production builds.
      console.info(
        '[refreshClaims] id_token (dev only):',
        withToken.idToken ??
          '(not on account — check MSAL cache / Network after redirect)'
      );
      console.info('[refreshClaims] id_token_claims (dev only):', {
        ...account.idTokenClaims
      });
    }

    queryClient.clear();
    clearSupplierClaimClientSession();

    // Local-only logout: clears MSAL's token/account cache in
    // sessionStorage / localStorage. Returning false from
    // onRedirectNavigate prevents the browser from navigating to the
    // B2C end-session endpoint, so the SSO cookie stays intact.
    try {
      await instance.logoutRedirect({
        account,
        onRedirectNavigate: () => false
      });
    } catch {
      // Expected — MSAL throws when redirect navigation is cancelled
    }

    // With the local cache empty, MSAL must go to B2C for a fresh token.
    // The SSO cookie means no login prompt — B2C silently re-runs the
    // policy and the Azure Function returns the new companyId.
    await instance.loginRedirect({
      scopes: ['openid', 'profile']
    });
  }, [instance, accounts, queryClient]);

  return { refreshClaims };
};
